import { getMedicaidAdultMinAge } from '@app/bookings/constants/medicaidStateConfig'
import { bookingPageRoute } from 'app/NewBookings/constants'
import { VIRTUAL_SLOT_ID_PREFIX } from 'app/ProviderSearch/constants'
import { getClientDetailsRouteFromCounselingTypeId } from 'components/app/NewBookings/utils'
import {
  bookingToolNumber,
  COOKIE_KEYS,
  dateFormats,
  partnersSupportTeamNumber,
  partnersSupportTeamNumberByCode,
  sessionStorageKeys,
} from 'constants/common'
import { pageRoutes } from 'constants/routing'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import {
  getAppointmentsWithAProvider,
  getAppointmentsWithProviderForCounselling,
} from 'dataSource/appointment/routes'
import { PATIENT_ACTIVE_INSURANCE_ROUTE } from 'dataSource/patients/routes'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { isToday, parseISO } from 'date-fns'
import format from 'date-fns/format'
import subDays from 'date-fns/subDays'
import subYears from 'date-fns/subYears'
import { FormikErrors } from 'formik'
import { RedirectLoginOptions } from 'hooks/useThriveAuth/types'
import Cookies from 'js-cookie'
import {
  getMarketingParamsQueryRecord,
  withMarketingParams,
} from 'lib/marketingParams'
import _every from 'lodash/every'
import _filter from 'lodash/filter'
import _isEmpty from 'lodash/isEmpty'
import _omit from 'lodash/omit'
import { NextRouter } from 'next/router'
import { default as qs, default as QueryString } from 'qs'
import { BaseSyntheticEvent, Dispatch, SetStateAction } from 'react'
import { Req } from 'use-http'
import { getPartnershipCodeFromSearchQuery } from 'utils/b2bDetails'
import {
  ErrorMessage,
  getBirthDate,
  getErrorMessage,
  getParsedQuery,
  hasError,
  isDateValid,
} from 'utils/common'
import {
  getAddressSchema,
  getDateOfBirthSchema,
  getDateOfBirthSchemaNoMinor,
  getFirstNameSchema,
  getLastNameSchema,
} from 'utils/commonYupSchemas'
import { getTreatment } from 'utils/treatment'
import * as Yup from 'yup'
import {
  AgeGroup,
  EMAIL_REG,
  Gender,
  MinRelationships,
  NAME_REG,
  NON_BINARY_DISPLAY_LABEL,
  NOT_ALPHABET_REG,
  OtherRelationships,
  PatientConsentTypes,
} from './constants'

export enum ConsentSource {
  SUPPORT = 'support_web',
  PATIENT = 'patient_web',
}

export { Gender }

export const getGenderOptions = (includeNonbinary = false): SelectItem[] => {
  let genderOptions = []

  for (const gender of Object.values(Gender)) {
    // Skip NON_BINARY option if feature flag is off
    if (gender === Gender.NON_BINARY && !includeNonbinary) {
      continue
    }

    // Use display label for Non-binary, backend value stays 'Non-binary'
    const label =
      gender === Gender.NON_BINARY ? NON_BINARY_DISPLAY_LABEL : gender

    genderOptions = [
      ...genderOptions,
      {
        label,
        value: gender,
      },
    ]
  }

  return genderOptions
}

export const getMinRelationships = (
  patient: PatientInfo,
  isSelfFromSelectedPatients: boolean,
  selfExists: boolean,
  counselingType: string
): string[] => {
  let relationshipTypes: MinRelationships[] = Object.values(
    MinRelationships
  ).filter((relationship) => relationship !== MinRelationships.SELF)

  //Add Self relationship type to the relationship type array of a patient
  //if anything or both from below holds true for that patient:
  //1. if there is no self patient at all from the existing as well
  //as the patients for that appointment
  //2. or if the selected patient is itself the MySelf one
  if (
    patient?.relationship === MinRelationships.SELF ||
    (!selfExists && !isSelfFromSelectedPatients)
  ) {
    relationshipTypes = [MinRelationships.SELF, ...relationshipTypes]
  }

  //If couples counseling, Child relationship is to be removed from relationship types array
  if (counselingType === MultiplePatientCounseling.COUPLES) {
    relationshipTypes = relationshipTypes.filter(
      (relationshipType) => relationshipType !== MinRelationships.CHILD
    )
  }

  return relationshipTypes
}

export const getSupportTeamNumber = (
  partnershipCode?: string | null,
  fallbackNumber?: PhoneNumberWithFormat
): PhoneNumberWithFormat => {
  const code = partnershipCode?.trim()

  if (code) {
    const partnerNumberByCode = partnersSupportTeamNumberByCode.find(
      (p) => p.partnershipCode === code
    )

    return partnerNumberByCode || partnersSupportTeamNumber
  }

  return fallbackNumber || bookingToolNumber
}

export const getOtherRelationships = (): SelectItem[] => {
  return Object.values(OtherRelationships).map((relationship) => ({
    label: relationship,
    value: relationship,
  }))
}

export const getMappedPatientRelation = (patientRelation: string): string => {
  if (
    !patientRelation ||
    patientRelation === MinRelationships.SELF ||
    patientRelation === 'Myself' || // Salesforce
    patientRelation === 'Spouse' ||
    patientRelation === 'Child'
  ) {
    return patientRelation
  }

  return 'Other'
}

export const relationshipsThatAllowMinor = {
  Child: 'Child',
  'Grandson or Granddaughter': 'GrandChild',
  'Stepson or Stepdaughter': 'Stepchild',
}

export const checkSelfExists = (
  patients: PatientInfo[],
  currentPatientId = ''
): boolean => {
  const foundPatient = patients.find(
    (patient) => patient.relationship === MinRelationships.SELF
  )

  if (!foundPatient || foundPatient.id === currentPatientId) {
    return false
  }

  return true
}

export const doesInsuranceMatchPatient = (
  insurance: InsuranceDetails,
  patient: PatientInfo
): boolean => {
  const patientInInsurance = insurance.patient

  return patientInInsurance.id === patient.id
}

export const getSelectedInsuranceProvider = (
  providerDetails: Provider,
  selectedInsurance: InsuranceDetails
): InsuranceProvider =>
  providerDetails.practice.insurances?.find(
    (providerInsurance) =>
      providerInsurance.insuranceProvider.id ===
      selectedInsurance?.insuranceProvider.id
  )?.insuranceProvider

export const supportUserSchema = Yup.object().shape({
  userEmail: Yup.string()
    .matches(EMAIL_REG, 'Invalid Email')
    .required('Email is required'),
})

export const updateEmailSchema = Yup.object().shape({
  emailId: Yup.string()
    .matches(EMAIL_REG, 'Invalid Email')
    .required('Email is Required'),
})

export const appointmentDetailsSchema = Yup.object().shape({
  counselingTypeId: Yup.string().required('Please select a service type'),

  appointmentMode: Yup.string().required('Please select the mode of session'),
})

export const getPatientInfoFormSchema = (
  isSupportOrThirdPartyUser?: boolean
): Yup.AnyObjectSchema => {
  return Yup.object().shape({
    patients: Yup.array().of(
      Yup.object().shape({
        firstName: getFirstNameSchema(isSupportOrThirdPartyUser),
        lastName: getLastNameSchema(isSupportOrThirdPartyUser),
        email: Yup.string()
          .required(
            getErrorMessage(
              ErrorMessage.REQUIRED,
              'Email Address',
              isSupportOrThirdPartyUser
            )
          )
          .max(255, getErrorMessage(ErrorMessage.MAX, 'Email Address'))
          .matches(
            EMAIL_REG,
            getErrorMessage(ErrorMessage.NOT_VALID, 'Email Address')
          )
          .test(
            'empty_check',
            getErrorMessage(ErrorMessage.NOT_VALID, 'Email Address'),
            (email) => email?.trim().length !== 0
          )
          .nullable(),
        dateOfBirth: getDateOfBirthSchema(isSupportOrThirdPartyUser).when(
          'relationship',
          {
            is: (relationship: string) =>
              !relationshipsThatAllowMinor[relationship],
            then: () => getDateOfBirthSchemaNoMinor(isSupportOrThirdPartyUser),
          }
        ),
        relationship: Yup.string()
          .required(
            getErrorMessage(
              ErrorMessage.REQUIRED,
              'relationship',
              isSupportOrThirdPartyUser
            )
          )
          .nullable(),
        phoneNumberValid: Yup.boolean()
          .oneOf([true], 'Please enter a valid phone number')
          .required(
            getErrorMessage(
              ErrorMessage.REQUIRED,
              'phone number',
              isSupportOrThirdPartyUser
            )
          ),
      })
    ),
  })
}

const getMemberIdFieldSchema = (
  isSupportOrThirdPartyUser: boolean,
  subscriberInputLabel: string,
  subscriberIdInputOptional: boolean
) => {
  const label = subscriberInputLabel

  if (subscriberIdInputOptional) {
    return Yup.string()
      .transform((value) => (typeof value === 'string' ? value.trim() : ''))
      .test('memberId', '', function (value) {
        if (!value) {
          return true
        }

        if (value.length < 4) {
          return this.createError({
            message: `${label} must be at least 4 characters`,
          })
        }

        if (value.length > 25) {
          return this.createError({
            message: `${label} cannot be longer than 25 characters`,
          })
        }

        if (!/^[a-zA-Z0-9]+$/.test(value)) {
          return this.createError({
            message: `${label} must be alphanumeric`,
          })
        }

        return true
      })
  }

  return Yup.string()
    .required(
      getErrorMessage(ErrorMessage.REQUIRED, label, isSupportOrThirdPartyUser)
    )
    .min(4, `${label} must be at least 4 characters`)
    .max(25, `${label} cannot be longer than 25 characters`)
    .matches(/^[a-zA-Z0-9]+$/, `${label} must be alphanumeric`)
}

const getPersonalInformationSchema = (
  isSupportOrThirdPartyUser: boolean,
  priorAuthConfiguration: PriorAuthConfiguration
): Yup.AnyObjectSchema => {
  const {
    showSubscriberIdInput,
    subscriberInputLabel,
    subscriberIdInputOptional = false,
  } = priorAuthConfiguration

  return Yup.object().shape({
    gender: Yup.string().required(
      getErrorMessage(
        ErrorMessage.REQUIRED,
        'patient gender',
        isSupportOrThirdPartyUser
      )
    ),
    ...(showSubscriberIdInput && {
      memberId: getMemberIdFieldSchema(
        isSupportOrThirdPartyUser,
        subscriberInputLabel ?? 'Member/Subscriber ID',
        subscriberIdInputOptional
      ),
    }),
  })
}

const getReferralInformationSchema = (
  isSupportOrThirdPartyUser: boolean,
  priorAuthConfiguration: PriorAuthConfiguration
): Yup.AnyObjectSchema => {
  const { showReferredBy, showReferralReason } = priorAuthConfiguration

  return Yup.object().shape({
    ...(showReferredBy && {
      referringProviderEhrId: Yup.number().required(
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'referring provider',
          isSupportOrThirdPartyUser
        )
      ),
    }),
    ...(showReferralReason && {
      referralReason: Yup.string().required(
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'referral reason',
          isSupportOrThirdPartyUser
        )
      ),
    }),

    effectiveDate: Yup.string()
      .required(
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'effective date',
          isSupportOrThirdPartyUser
        )
      )
      .test('valid_check', 'Please enter a valid date', (value) =>
        isDateValid(value)
      ),
    expirationDate: Yup.string()
      .required(
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'expiration date',
          isSupportOrThirdPartyUser
        )
      )
      .test('valid_check', 'Please enter a valid date', (value) =>
        isDateValid(value)
      )
      .test(
        'past_date_check',
        `Expiration date must be later than ${format(
          subDays(new Date(), 1),
          dateFormats.DAY_MONTHNAME_YEAR_FORMAT
        )}`,
        (value) =>
          new Date(value).setHours(0, 0, 0, 0) >=
          new Date().setHours(0, 0, 0, 0)
      )
      .test(
        'min_check_after_effective_date',
        'Expiration date must be later than the effective date',
        (value, { parent }) =>
          isDateValid(parent.effectiveDate)
            ? new Date(value).setHours(0, 0, 0, 0) >=
              new Date(parent.effectiveDate).setHours(0, 0, 0, 0)
            : true
      ),
  })
}

const getAuthorizationInformationSchema = (
  isSupportOrThirdPartyUser: boolean,
  priorAuthConfiguration: PriorAuthConfiguration
): Yup.AnyObjectSchema =>
  Yup.object().shape({
    authorizationNumber: Yup.string().required(
      getErrorMessage(
        ErrorMessage.REQUIRED,
        'authorization number',
        isSupportOrThirdPartyUser
      )
    ),
    authorizationDescription: Yup.string()
      .required(
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'authorization description',
          isSupportOrThirdPartyUser
        )
      )
      .max(50, 'Authorization decription cannot be longer than 50 characters.'),

    ...(priorAuthConfiguration.showSessionLimitCapture && {
      hasSessionLimit: Yup.boolean()
        .oneOf([true, false])
        .required('Please select whether the patient has a session limit'),
      sessionLimit: Yup.string().when('hasSessionLimit', {
        is: true,
        then: Yup.string()
          .matches(
            /^[1-9]\d*$/,
            'Session limit must be a number greater than 0'
          )
          .required('Please enter the session limit'),
        otherwise: Yup.string().optional().nullable(),
      }),
    }),
  })

const getAuthorizationInsuranceCarrierFieldSchema = (
  isSupportOrThirdPartyUser?: boolean
): Yup.NumberSchema =>
  Yup.number().required(
    getErrorMessage(
      ErrorMessage.REQUIRED,
      'referring provider',
      isSupportOrThirdPartyUser
    )
  )

export const getAuthorizationDetailsFormSchema = (
  showInsuranceCarrierField = false,
  showPersonalInfoFields = false,
  isSupportOrThirdPartyUser = false,
  priorAuthConfiguration: PriorAuthConfiguration
): Yup.SchemaOf<unknown> => {
  let modifiedReferralInformationSchema = getReferralInformationSchema(
    isSupportOrThirdPartyUser,
    priorAuthConfiguration
  )

  if (showInsuranceCarrierField) {
    modifiedReferralInformationSchema = modifiedReferralInformationSchema.concat(
      Yup.object().shape({
        insuranceCarrier: getAuthorizationInsuranceCarrierFieldSchema(
          isSupportOrThirdPartyUser
        ),
      })
    )
  }

  return Yup.object().shape({
    authorizationFormData: Yup.object().shape({
      ...(!!showPersonalInfoFields && {
        personalInformation: getPersonalInformationSchema(
          isSupportOrThirdPartyUser,
          priorAuthConfiguration
        ),
      }),
      referralInformation: modifiedReferralInformationSchema,
      authorizationInformation: getAuthorizationInformationSchema(
        isSupportOrThirdPartyUser,
        priorAuthConfiguration
      ),
    }),
  })
}

export const getPaymentBillingSchema = (
  isSupportOrThirdPartyUser?: boolean
): Yup.AnyObjectSchema =>
  Yup.object().shape({
    firstName: getFirstNameSchema(isSupportOrThirdPartyUser),
    lastName: getLastNameSchema(isSupportOrThirdPartyUser),
    address: getAddressSchema(isSupportOrThirdPartyUser),
  })

export const paymentCardDetailsSchema = Yup.object().shape({
  paymentCardNumber: Yup.boolean()
    .oneOf([true], getErrorMessage(ErrorMessage.NOT_VALID, 'card number'))
    .required(getErrorMessage(ErrorMessage.REQUIRED, 'card number')),
  paymentCardExpiry: Yup.boolean()
    .oneOf(
      [true],
      getErrorMessage(ErrorMessage.NOT_VALID, `card's expiry date`)
    )
    .required(getErrorMessage(ErrorMessage.REQUIRED, `card's expiry date`)),
  paymentCardCVC: Yup.boolean()
    .oneOf([true], getErrorMessage(ErrorMessage.NOT_VALID, `card's CVC Code`))
    .required(getErrorMessage(ErrorMessage.REQUIRED, `card's CVC Code`)),
})

export enum MultiplePatientCounseling {
  FAMILY = 'Family Counseling',
  COUPLES = 'Couples/Marriage Counseling',
}

export enum PatientCounseling {
  INDIVIDUAL_COUNSELING = 'Individual Counseling',
}

export const emptyPatient: PatientInfo = {
  firstName: '',
  lastName: '',
  gender: '',
  dateOfBirth: '',
  relationship: '',
  phoneNumber: '',
  email: '',
  isExisting: false,
  hasRelationship: false,
  hasEmail: false,
  otherRelationshipTabSelected: false,
}

export const getAgeGroupsFromProvider = (
  providerDetails: Provider
): string[] => {
  const ageGroups = providerDetails?.filters
    ?.filter((filter) => filter.type === AgeGroup.AGE_GROUP)
    ?.sort((a, b) => parseInt(a.value) - parseInt(b.value))
    ?.map((group) => group.value)

  return ageGroups
}

export const ageGroupServed = (ageGroups: string[]): number[][] => {
  if (!_isEmpty(ageGroups)) {
    let index = 0

    // Here we are creating an array consisiting of the age groups served by the provider
    // We club age groups like 4-6 and 7-9 into 4-9 in order to get a smaller array to
    // compare the seleced DOB age with age groups served by the provider
    const ageGroupMapping = ageGroups.reduce((acc, ageGroup) => {
      if (ageGroup.includes('-')) {
        const splitGroup = ageGroup.split('-')
        const upperLimit = parseInt(splitGroup[1])
        const lowerLimit = parseInt(splitGroup[0])

        if (index > 0 && lowerLimit - acc[index - 1][1] === 1) {
          const temp = [acc[index - 1][0], upperLimit]
          acc[index - 1] = temp
          return acc
        } else {
          index++
          return [...acc, [lowerLimit, upperLimit]]
        }
      } else if (ageGroup.includes('+')) {
        const splitGroup = ageGroup.split('+')
        const lowerLimit = parseInt(splitGroup[0])
        return [...acc, [lowerLimit, 299]]
      }
    }, [])
    return ageGroupMapping
  }
}

export const validateAndProceedBooking = (
  isAuthenticated: boolean,
  loginWithRedirect: (options?: RedirectLoginOptions) => void,
  router: NextRouter,
  // eslint-disable-next-line
  queryObject: any
): void => {
  const isBookingDisabled =
    getTreatment(TreatmentFlags.THRIVEWEB_BOOKINGS) ===
    treatmentFlagValues.DISABLE
  const isB2bPartnerSelfBookingOn =
    getTreatment(TreatmentFlags.B2B_PARTNERSHIP_SELF_BOOKING) ===
    treatmentFlagValues.ON

  if (isBookingDisabled) {
    router.push(pageRoutes.UNDER_MAINTENANCE)
  } else if (isAuthenticated) {
    router.push({
      pathname: pageRoutes.BOOKING,
      query: {
        ...queryObject,
        ...getMarketingParamsQueryRecord(),
      },
    })
  } else {
    //user or support user has not yet authenticated and clicks on booking slot
    const {
      counselingType,
      availabilityType,
      partnershipCode,
    } = getParsedQuery(router)
    const modifiedQuery = {
      /*
      Adding additional query params which will go to magic link redirectTo query params.
      So that we can prefill data on Patient-info page
      */
      ...(!!counselingType && {
        counselingType:
          typeof counselingType === 'string'
            ? counselingType
            : counselingType[0],
        ...(!!availabilityType && {
          availabilityType: availabilityType?.[0],
        }),
      }),
      ...queryObject,
      ...(isB2bPartnerSelfBookingOn && partnershipCode && { partnershipCode }),
    }
    const queryParamString = QueryString.stringify(modifiedQuery, {
      addQueryPrefix: true,
    })
    const isLoginModalOverBookingV2On =
      getTreatment(TreatmentFlags.LOGIN_MODAL_OVER_BOOKING_V2) ===
      treatmentFlagValues.ON

    const querySeparator = queryParamString ? '&' : '?'
    const baseReturnTo = `${pageRoutes.BOOKING}${queryParamString}${querySeparator}showSuccessModal=true`
    const marketingQuery = getMarketingParamsQueryRecord()
    const returnTo = withMarketingParams(baseReturnTo)

    if (isLoginModalOverBookingV2On) {
      router.push({
        pathname: bookingPageRoute.INITIAL,
        query: {
          returnTo,
          ...marketingQuery,
        },
      })
    } else {
      router.push({
        pathname: pageRoutes.VERIFY_USER_IDENTITY,
        query: {
          returnTo,
          ...marketingQuery,
        },
      })
    }
  }
}

export interface BookingFlowRedirectionParams {
  user: Partial<User>
  isActiveB2BUser: boolean
  isSmallSize: boolean
  globalGroupedFilters: GroupedProviderFilter | null
  query?: Record<string, string | string[]> // Router query parameters
  b2bUserDetails?: B2BDetails
}

/**
 * Handles v2 booking flow redirection logic
 * @param params - Parameters needed for redirection decision
 * @returns boolean - true if v2 redirection was performed, false if v1 flow should continue
 */
export const handleBookingFlowRedirection = ({
  user,
  isActiveB2BUser,
  isSmallSize,
  globalGroupedFilters,
  query,
  b2bUserDetails,
}: BookingFlowRedirectionParams): boolean => {
  // Check for v2 booking flow treatment enabled for D2C clients
  const isBookingV2EnabledForD2C =
    getTreatment(TreatmentFlags.BOOKING_V2, {
      email: user.email,
    }) === treatmentFlagValues.NEW

  const partnershipCode =
    b2bUserDetails?.keyword || getPartnershipCodeFromSearchQuery()

  const isB2BUser = isActiveB2BUser || !!partnershipCode

  // Check for v2 booking flow treatment enabled for self partnership users
  const isBookingV2EnabledForSelfPartnership =
    getTreatment(TreatmentFlags.BOOKING_V2_SELF_PARTNERSHIP, {
      email: user.email,
    }) === treatmentFlagValues.NEW

  const shouldUseBookingV2 = isB2BUser
    ? isBookingV2EnabledForSelfPartnership
    : isBookingV2EnabledForD2C

  if (shouldUseBookingV2) {
    // Extract login success params from nested returnTo (verify-user) or
    // top-level query (post-auth /app/bookings after returnTo is unwrapped)
    const getQueryParam = (value: string | string[] | undefined) =>
      Array.isArray(value) ? value[0] : value

    let showSuccessModal: string | null = null
    let googleSSOLogin: string | null = null

    const returnToString = getQueryParam(query?.returnTo)

    if (returnToString) {
      try {
        // Use current origin as base URL for relative paths
        const baseUrl =
          typeof window !== 'undefined'
            ? window.location.origin
            : 'https://thriveworks.com' // fallback for SSR
        const returnToUrl = new URL(returnToString, baseUrl)
        showSuccessModal = returnToUrl.searchParams.get('showSuccessModal')
        googleSSOLogin = returnToUrl.searchParams.get('googleSSOLogin')
      } catch (error) {
        // Final fallback: simple string matching
        if (returnToString.includes('showSuccessModal=true')) {
          showSuccessModal = 'true'
          googleSSOLogin = returnToString.includes('googleSSOLogin=true')
            ? 'true'
            : 'false'
        }
      }
    } else {
      showSuccessModal = getQueryParam(query?.showSuccessModal) ?? null
      googleSSOLogin = getQueryParam(query?.googleSSOLogin) ?? null
    }

    const existingPreBookingSelection = getPreBookingSelectionFromCookies()

    Cookies.set(
      COOKIE_KEYS.PRE_BOOKING_SELECTION,
      JSON.stringify({
        ...existingPreBookingSelection,

        // Add login success info to the existing cookie
        ...(showSuccessModal === 'true' && {
          loginSuccessInfo: {
            showSuccessModal: 'true',
            googleSSOLogin: googleSSOLogin === 'true' ? 'true' : 'false',
          },
        }),

        // Add partnership code to the existing cookie
        ...(!isActiveB2BUser && partnershipCode && { partnershipCode }),
      })
    )

    // These routes are navigating to the app router
    // for some reason the router doesn't seem to replace the url if it is navigating to a app router route
    if (isSmallSize) {
      window.location.replace(withMarketingParams(bookingPageRoute.INITIAL))
    } else if (!!globalGroupedFilters?.[ProviderFilterTypes.COUNSELING_TYPE]) {
      const preBookingSelection = JSON.parse(
        Cookies.get(COOKIE_KEYS.PRE_BOOKING_SELECTION) || '{}'
      )
      const { counselingTypeId } = preBookingSelection
      const clientDetailsRoute = getClientDetailsRouteFromCounselingTypeId(
        counselingTypeId,
        globalGroupedFilters
      )
      window.location.replace(withMarketingParams(clientDetailsRoute))
    } else {
      window.location.replace(withMarketingParams('/bookings'))
    }

    return true // Indicates that v2 redirection was performed
  }

  return false // Indicates that v1 flow should continue
}

export const handleNameChange = (
  event: BaseSyntheticEvent,
  name: string,
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => void | Promise<void | FormikErrors<any>>
): void => {
  const {
    target: { value },
  } = event
  let inputName = value

  //if the value string has only alphabets then set this as field value
  //else set the field by replacing with the alphabetic part of the value string
  if (value !== '' && !NAME_REG.test(value)) {
    inputName = value.replace(NOT_ALPHABET_REG, '')
  }

  setFieldValue(name, inputName)
}

export const getPayRate = (
  providerDetails: Provider,
  multipleAppointmentsWithProvider: boolean
): number => {
  const followupPayRate = providerDetails?.about?.payrates.follow_up
  const individualPayRate = providerDetails?.about?.payrates.individual

  if (multipleAppointmentsWithProvider) {
    return followupPayRate ?? individualPayRate
  }

  return individualPayRate
}

export const validateResponsiblePartyData = (user: Partial<User>): boolean => {
  if (
    user?.firstName &&
    user?.lastName &&
    user?.dob &&
    user?.address?.line1 &&
    user?.address?.city &&
    user?.address?.zipcode &&
    user?.address?.state &&
    user?.phoneNumber &&
    user?.responsiblePartyEhrId
  ) {
    return true
  } else {
    return false
  }
}

export const createSelfPatientFromResponsibleParty = (
  user: UserExistsResponse
): Partial<PatientInfo> => {
  return {
    ..._omit(user, [
      'businessPartnerId',
      'isPartnerAssociationActive',
      'auth0UserId',
      'userExists',
      'businessPartner',
      'id',
    ]),
    isExisting: true,
    phoneNumberValid: true,
    dateOfBirth: user.dob,
  }
}

export const createResponsiblePartyFromUserData = (
  user: Partial<User>
): UserExistsResponse => {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    dob: user.dob,
    address: user.address,
    businessPartner: user?.businessPartner,
    userExists: true,
    phoneNumber: user.phoneNumber,
    email: user.email,
    id: user.id,
    responsiblePartyEhrId: user.responsiblePartyEhrId,
  }
}

export const setSelfPatientValues = (
  setFieldValue: (key: string, value: string) => void,
  patientIndex: number,
  values: Partial<PatientInfo>,
  setFieldTouched?: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) => Promise<void> | Promise<FormikErrors<PatientInfoFormValues>>
): void => {
  Object.keys(values).map((key) => {
    setFieldValue(`patients[${patientIndex}][${key}]`, values[key])
    !!setFieldTouched &&
      setFieldTouched(`patients[${patientIndex}][${key}]`, false)
  })

  if (!!values.phoneNumber) {
    setFieldValue(`patients[${patientIndex}].isExisting`, 'true')
  }
}

export const setPatientData = (
  setFieldValue: (key: string, value: string) => void,
  patientIndex: number,
  values: Partial<PatientInfo>,
  setFieldTouched?: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) => Promise<void> | Promise<FormikErrors<PatientInfoFormValues>>
): void => {
  setFieldValue(`patients[${patientIndex}]`, null)
  Object.keys(values).map((key) => {
    setFieldValue(
      `patients[${patientIndex}][${key}]`,
      typeof values?.[key] !== 'undefined' || null ? values[key] : ''
    )
    !!setFieldTouched &&
      setFieldTouched(`patients[${patientIndex}][${key}]`, false)
  })

  if (!!values?.phoneNumber) {
    setTimeout(() => {
      setFieldValue(`patients[${patientIndex}].phoneNumberValid`, 'true')
    }, 0)
  }
}

export const mergeSelfPatientFromRp = (
  responsibleParty: UserExistsResponse,
  patient: PatientInfo
): PatientInfo => {
  const modifiedPatient = Object.keys(patient).reduce((acc, key) => {
    if (!patient[key] && !!responsibleParty?.[key]) {
      acc[key] = responsibleParty[key]
    } else {
      acc[key] = patient[key]
    }

    return acc
  }, {} as PatientInfo)

  return modifiedPatient
}

export const getValidatedPatientList = (
  response: PatientInfo[],
  isValidResponsibleParty: boolean,
  responsibleParty: UserExistsResponse
): PatientInfo[] => {
  const { selfPatientIndex, selfPatient } = getSelfPatientAndIndex(response)

  if (!!selfPatient && isValidResponsibleParty) {
    const isValidSelfPatient = validateResponsiblePartyData(selfPatient)

    if (isValidSelfPatient) {
      return response
    } else {
      const modifiedPatientList = response
      modifiedPatientList[selfPatientIndex] = mergeSelfPatientFromRp(
        responsibleParty,
        selfPatient
      )
      return modifiedPatientList
    }
  } else {
    return response
  }
}

export const isSelf = (relationship: string): boolean => {
  return relationship === MinRelationships.SELF
}

export const isChild = (relationship: string): boolean => {
  return relationship === MinRelationships.CHILD
}

export const isMinor = (patient: PatientInfo): boolean => {
  const date = getBirthDate(patient.dateOfBirth)
  return subYears(new Date(), 18) < date
}

export const isMedicaidMinor = (
  patient: PatientInfo,
  stateCode?: string
): boolean => {
  if (!patient?.dateOfBirth) {
    return true
  }

  const dob = getBirthDate(patient.dateOfBirth)

  return subYears(new Date(), getMedicaidAdultMinAge(stateCode)) < dob
}

export const isMedicaidMinorBookingBlocked = (
  isAcceptedMedicaidClient: boolean,
  selectedPatients: PatientInfo[],
  stateCode: string,
  isMinorBookingEnabled = false
): boolean =>
  isAcceptedMedicaidClient &&
  !isMinorBookingEnabled &&
  selectedPatients.some((patient) => isMedicaidMinor(patient, stateCode))

export const getRequiredConsents = (
  patients: PatientInfo[]
): ConsentDetails<PatientConsentTypes>[] => {
  let requiredConsents = []
  patients.forEach((patient) => {
    if (isSelf(patient.relationship) || isMinor(patient)) {
      const validConsents = patient.consents?.filter(
        (consent) =>
          consent.isConsentProvided &&
          consent.consentType === PatientConsentTypes.PRACTICE &&
          consent.source === ConsentSource.PATIENT
      )
      const areValidConsentsPresent = validConsents?.length > 0
      requiredConsents = requiredConsents.concat(
        areValidConsentsPresent ? validConsents : {}
      )
    }
  })
  return requiredConsents
}

export const hasValidConsent = (
  consents: ConsentDetails<PatientConsentTypes>[]
): boolean => {
  if (!consents) {
    return false
  }

  return _filter(consents, (consent) => consent.isConsentProvided).length > 0
}

export const getAllMinors = (patients: PatientInfo[]): PatientInfo[] => {
  return _filter(patients, (patient) => isMinor(patient))
}

export const showConsentee = (allMinors: PatientInfo[]): string => {
  return allMinors.length === 1
    ? getFullName(allMinors[0].firstName, allMinors[0].lastName)
    : null
}

export const getFullName = (firstName: string, lastName: string): string => {
  return !!firstName && !!lastName
    ? `${firstName.trim()} ${lastName?.trim()}`
    : ''
}

export const getSelfPatientAndIndex = (
  patients: PatientInfo[]
): { selfPatient: PatientInfo; selfPatientIndex: number } => {
  return patients.reduce(
    (acc, patient, index) => {
      if (isSelf(patient.relationship)) {
        acc.selfPatient = patient
        acc.selfPatientIndex = index
      }

      return acc
    },
    { selfPatient: null, selfPatientIndex: -1 }
  )
}

export const getSelfPatientIndex = (patients: PatientInfo[]): number => {
  if (patients.length === 0) {
    return -1
  }

  return patients.findIndex((patient) => isSelf(patient.relationship))
}

export const validateConsentsForPatients = (
  patients: PatientInfo[]
): boolean => {
  const requiredConsents = getRequiredConsents(patients)
  return validateAllConsents(requiredConsents)
}

export const validateAllConsents = (
  consents: ConsentDetails<PatientConsentTypes>[]
): boolean => {
  /**
   * Implementing a explicit if since _every returns true for an empty array since it's truthy
   */
  if (consents.length > 0) {
    const areAllRequiredConsentsValid = _every(consents, {
      isConsentProvided: true,
      consentType: PatientConsentTypes.PRACTICE,
    })

    return areAllRequiredConsentsValid
  }

  return false
}

export const checkMultipleAppointmentsWithProvider = async (
  appointmentsWithProviderRequest: Req,
  patientId: string,
  providerId: string,
  counselingTypeId = ''
): Promise<boolean> => {
  const appointmentWithProviderRequestRoute = !!counselingTypeId
    ? getAppointmentsWithProviderForCounselling(
        patientId,
        providerId,
        counselingTypeId
      )
    : getAppointmentsWithAProvider(patientId, providerId)

  const response = await appointmentsWithProviderRequest.get(
    appointmentWithProviderRequestRoute
  )

  hasError(appointmentsWithProviderRequest, response)

  return !!counselingTypeId ? !!response : !!response?.appointmentsExist
}

/**
 * Creates a function that updates a specific key in the state object using the setter function from useState.
 * @template T The type of the state object.
 * @param {Dispatch<SetStateAction<T>>} setter - The state setter function from useState.
 * @returns {(key: keyof T, value: T[keyof T]) => void} A function that takes a key and a value, and updates the state.
 */
export const stateObjUpdater = <T>(
  setter: Dispatch<SetStateAction<T>>
): ((key: keyof T, value: T[keyof T]) => void) => {
  return <T>(key: keyof T, value: T[keyof T]): void => {
    setter((prevState) => ({
      ...prevState,
      [key]: value,
    }))
  }
}

const isInsuranceEligibleForSelfPay = (
  insuranceDetails: InsuranceDetails
): boolean =>
  insuranceDetails?.internalOutcomeAssessment ===
    'CARD_NOT_ACCEPTED_SELF_PAY_REQUIRED' ||
  insuranceDetails?.insuranceInquiryAudits?.[0]?.internalOutcomeAssessment ===
    'CARD_NOT_ACCEPTED_SELF_PAY_REQUIRED'

export const userInsurancesEligibleForSelfPayOption = (
  canUsersWithOutOfNetworkInsuranceSwithToSelfPay: boolean,
  userInsurances: InsuranceDetails[],
  patients: PatientInfo[]
): boolean =>
  canUsersWithOutOfNetworkInsuranceSwithToSelfPay &&
  userInsurances.some(
    (insurance) =>
      isInsuranceEligibleForSelfPay(insurance) &&
      patients.some((patient) => doesInsuranceMatchPatient(insurance, patient))
  )

export const getBeginningConsentCategory = (
  hasInsurance: boolean,
  areUserInsurancesEligibleForSelfPayOption: boolean
): ConsentCategory => {
  if (areUserInsurancesEligibleForSelfPayOption) {
    return 'switchToSelfPay'
  }

  if (hasInsurance) {
    return 'insurance'
  }

  return 'selfPay'
}

export const getToggleConsentCategory = (
  isSelfPay: boolean,
  areUserInsurancesEligibleForSelfPayOption: boolean
): ConsentCategory => {
  if (areUserInsurancesEligibleForSelfPayOption && isSelfPay) {
    return 'switchToSelfPay'
  }

  if (isSelfPay) {
    return 'selfPay'
  }

  return 'insurance'
}

export const isMedicaidInsurance = (
  insuranceDetails: MedicaidInsuranceDetails
): boolean => {
  const recentApiCallAssessment = insuranceDetails?.internalOutcomeAssessment
  const recentAuditLog = insuranceDetails?.insuranceInquiryAudits?.[0]

  const internalOutcomeAssessment =
    recentApiCallAssessment ?? recentAuditLog?.internalOutcomeAssessment

  const isMedicaid =
    insuranceDetails?.isMedicaid ??
    recentAuditLog?.summary?.insurance?.isMedicaid

  return internalOutcomeAssessment === 'DENIED_DUE_TO_MEDICAID' || !!isMedicaid
}

export const getIsDeniedDueToMedicaid = (
  insuranceDetails: InsuranceDetails,
  // default param while loading
  selectedPatients: PatientInfo[],
  shouldCheckAuditCreatedBeforeToday = false
): boolean => {
  const isDeniedDueToMedicaid = isMedicaidInsurance(insuranceDetails)

  const isThisBookingForSelectedPatient: boolean = selectedPatients?.some(
    (patient: PatientInfo): boolean =>
      doesInsuranceMatchPatient(insuranceDetails, patient)
  )

  if (shouldCheckAuditCreatedBeforeToday) {
    const mostRecentAuditCreatedAt =
      insuranceDetails?.insuranceInquiryAudits?.[0]?.createdAt

    if (mostRecentAuditCreatedAt) {
      const auditDate = parseISO(mostRecentAuditCreatedAt)
      const isAuditCreatedBeforeToday = !isToday(auditDate)
      return (
        isDeniedDueToMedicaid &&
        isThisBookingForSelectedPatient &&
        isAuditCreatedBeforeToday
      )
    } else {
      return false
    }
  }

  return isDeniedDueToMedicaid && isThisBookingForSelectedPatient
}

export const synchronizePatientInfoWithUpdatedData = (
  patients: PatientInfo[],
  updatedPatients: PatientInfo[]
): PatientInfo[] => {
  return patients.map((patient) => {
    const matchingPatient = updatedPatients.find(
      (updatedPatient) => updatedPatient.id === patient.id
    )

    if (matchingPatient) {
      // Only update properties that exist in patient
      Object.keys(patient).forEach((key) => {
        if (matchingPatient.hasOwnProperty(key)) {
          patient[key] = matchingPatient[key]
        }
      })
    }

    return patient
  })
}

const getPatientIds = (allPatients: PatientDetails[]): string[] =>
  allPatients.map((patient) => patient.ehrId).filter((patientId) => !!patientId)

export const fetchActiveInsuranceData = async (
  patients: (PatientDetails | PatientInfo)[],
  activeInsuranceRequest: Req<unknown>
): Promise<PatientActiveInsurance[]> => {
  const patientEhrIds = getPatientIds(patients)
  const licenseKey = patients?.[0]?.licenseKey
  const requestUrl = `${PATIENT_ACTIVE_INSURANCE_ROUTE}?${qs.stringify(
    {
      patientEhrIds: patientEhrIds,
      licenseKey,
    },
    {
      indices: false,
      encode: false,
    }
  )}`

  if (patientEhrIds?.length > 0) {
    const allPatientActiveInsurances: PatientActiveInsurance[] = await activeInsuranceRequest.get(
      requestUrl
    )
    return allPatientActiveInsurances
  }
}

export const fetchDoAllPatientsHaveCurrentInsurance = async (
  patients: (PatientDetails | PatientInfo)[],
  activeInsuranceRequest: Req<unknown>
): Promise<boolean> => {
  const allPatientActiveInsurances = await fetchActiveInsuranceData(
    patients,
    activeInsuranceRequest
  )

  if (allPatientActiveInsurances?.length !== patients?.length) {
    return false
  }

  return !allPatientActiveInsurances.some(
    (patientActiveInsurance) =>
      patientActiveInsurance?.carrierCode === 'SELFPAY' ||
      patientActiveInsurance.carrierName.includes('SELF PAY')
  )
}

export const joinStrings = (strings: string[]): string => {
  return strings.filter(Boolean).join(' ').trim()
}

// Helper function to get pre-booking selection from cookie
// eslint-disable-next-line
export const getPreBookingSelectionFromCookies = (): Record<string, any> => {
  try {
    return JSON.parse(Cookies.get(COOKIE_KEYS.PRE_BOOKING_SELECTION) || '{}')
  } catch {
    return {}
  }
}

/**
 * Returns the primary patient from the list, preferring the one with relationship 'SELF'.
 * Falls back to the first patient if none match.
 *
 * @param selectedPatients - Array of selected patients
 * @returns The primary patient, or undefined if the list is empty
 */
export const getPrimaryPatient = (
  selectedPatients: PatientInfo[]
): PatientInfo => {
  // Guard against non-array inputs (e.g. an API error body that slipped
  // through as data). Optional chaining only covers null/undefined; a non-null
  // object would still reach `.find` and throw `find is not a function`.
  const patients = Array.isArray(selectedPatients) ? selectedPatients : []
  return (
    patients.find(
      (patient) => patient.relationship === MinRelationships.SELF
    ) || patients[0]
  )
}

/**
 * Checks if a single insurance is an accepted Medicaid insurance
 * @param insurance - The insurance details to check
 * @returns true if the insurance is accepted Medicaid
 */
export const getIsAcceptedMedicaidInsurance = (
  insurance: InsuranceDetails
): boolean => {
  const recentApiCallAssessment = insurance?.internalOutcomeAssessment
  const recentAuditLog = insurance?.insuranceInquiryAudits?.[0]

  const internalOutcomeAssessment =
    recentApiCallAssessment ?? recentAuditLog?.internalOutcomeAssessment

  return (
    isMedicaidInsurance(insurance) && internalOutcomeAssessment === 'ACCEPTED'
  )
}

export const getPrimaryPatientHasAcceptedMedicaidInsurance = (
  userInsurances: InsuranceDetails[],
  selectedPatients: PatientInfo[]
): boolean => {
  const primaryPatient = getPrimaryPatient(selectedPatients)

  if (!primaryPatient) {
    return false
  }

  return userInsurances?.some(
    (insurance) =>
      doesInsuranceMatchPatient(insurance, primaryPatient) &&
      getIsAcceptedMedicaidInsurance(insurance)
  )
}

/**
 * Determines whether the current session should be
 * treated as an accepted Medicaid session (to improve UX).
 * This flag is set after an accepted Medicaid insurance
 * is verified on the Verify Insurance page
 *
 * Note: An accepted Medicaid insurance must still be
 * added/present at billing step to book a Medicaid session.
 */
export const getIsAcceptedMedicaidSession = (): boolean => {
  return (
    sessionStorage.getItem(sessionStorageKeys.IS_ACCEPTED_MEDICAID_SESSION) ===
    'true'
  )
}

export const resetAcceptedMedicaidSession = (): void => {
  sessionStorage.removeItem(sessionStorageKeys.IS_ACCEPTED_MEDICAID_SESSION)
}

// Virtual slots have no slot row, so book directly from start/end instead of slotId.
export const getVirtualSlotBookingFields = (
  slotId: string
): { isVirtual: true } | { slotId: string } =>
  slotId?.startsWith(VIRTUAL_SLOT_ID_PREFIX) ? { isVirtual: true } : { slotId }

export const normalizeWhitespace = (str = ''): string =>
  str.trim().replace(/\s+/g, ' ')

export const getMedicaidAgeRestrictionMessage = (
  stateCode: string,
  adultMinAge: number
): string => {
  const stateLabel = stateCode ? `${stateCode}` : 'this state'
  return normalizeWhitespace(
    `We do not currently support booking in ${stateLabel} for Medicaid patients under ${adultMinAge}`
  )
}
